import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Loader2, LayoutGrid, List, Rows3, Boxes } from 'lucide-react';
import { Input } from '../components/ui/input';
import ProductCard from '../components/ProductCard';
import CategorySectionHeader from '../components/CategorySectionHeader';
import BrandSubHeader from '../components/BrandSubHeader';
import api, { absoluteAsset } from '../services/api';
import { sortCategories, groupByBrand } from '../services/sort';

const VIEW_KEY = 'tz_products_view';
const VIEWS = [
  { key: 'marcas', icon: Boxes, label: 'Marcas' },
  { key: 'grid', icon: LayoutGrid, label: 'Cuadrícula' },
  { key: 'compact', icon: Rows3, label: 'Compacta' },
  { key: 'list', icon: List, label: 'Lista' },
];

const Productos = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const categorySlug = searchParams.get('category') || 'all';
  const [query, setQuery] = useState('');
  const [view, setView] = useState(() => {
    try { return localStorage.getItem(VIEW_KEY) || 'marcas'; }
    catch (_e) { return 'marcas'; }
  });
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let on = true;
    Promise.all([api.get('/products'), api.get('/categories'), api.get('/brands')])
      .then(([p, c, b]) => {
        if (!on) return;
        setProducts(p.data.map(x => ({ ...x, image: absoluteAsset(x.image) })));
        setCategories(sortCategories(c.data));
        setBrands(b.data);
      })
      .finally(() => on && setLoading(false));
    return () => { on = false; };
  }, []);

  const changeView = (v) => {
    setView(v);
    try { localStorage.setItem(VIEW_KEY, v); } catch (_e) { /* ignore */ }
  };

  const brandByName = useMemo(() => {
    const map = {};
    brands.forEach(b => { map[b.name] = b; });
    return map;
  }, [brands]);

  const selectedCategory = useMemo(
    () => categories.find(c => c.slug === categorySlug),
    [categorySlug, categories]
  );

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const cats = selectedCategory ? [selectedCategory] : categories;
    return cats.map(cat => {
      const filtered = products
        .filter(p => p.category === cat.name)
        .filter(p =>
          !q ||
          p.name.toLowerCase().includes(q) ||
          (p.brand || '').toLowerCase().includes(q) ||
          (p.code || '').toLowerCase().includes(q)
        );
      // Sort alphabetically by brand then name for non-marcas views
      filtered.sort((a, b) =>
        (a.brand || '').localeCompare(b.brand || '', 'es') ||
        (a.name || '').localeCompare(b.name || '', 'es')
      );
      return {
        ...cat,
        items: filtered,
        brandGroups: groupByBrand(filtered),
      };
    });
  }, [query, selectedCategory, categories, products]);

  const totalShown = grouped.reduce((sum, c) => sum + c.items.length, 0);

  const setCat = (slug) => {
    if (slug === 'all') setSearchParams({});
    else setSearchParams({ category: slug });
  };

  // CSS grid class per view
  const gridClass =
    view === 'compact'
      ? 'grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6'
      : view === 'list'
        ? 'grid grid-cols-1 gap-3'
        : 'grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';

  const cardVariant = view === 'list' ? 'list' : view === 'compact' ? 'compact' : 'grid';

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">Productos</h1>
          {!loading && (
            <p className="mt-1 text-sm text-slate-600">
              {totalShown} {totalShown === 1 ? 'producto' : 'productos'}
              {query && ` para "${query}"`}
            </p>
          )}
        </div>
        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar productos..."
              className="h-11 w-full rounded-full border-slate-200 bg-white/80 pl-10 pr-4 text-slate-800 placeholder:text-slate-400 focus-visible:ring-emerald-500 sm:w-72"
            />
          </div>
          <div className="flex h-11 items-center gap-1 rounded-full border border-slate-200 bg-white/80 p-1">
            {VIEWS.map(({ key, icon: Icon, label }) => (
              <button
                key={key}
                onClick={() => changeView(key)}
                aria-label={label}
                title={label}
                className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
                  view === key
                    ? 'bg-emerald-200/70 text-emerald-900'
                    : 'text-slate-500 hover:bg-slate-100'
                }`}
              >
                <Icon className="h-4 w-4" />
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        <button
          onClick={() => setCat('all')}
          className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
            categorySlug === 'all'
              ? 'border-emerald-700 bg-emerald-700 text-white'
              : 'border-slate-200 bg-white text-slate-700 hover:border-emerald-300 hover:text-emerald-800'
          }`}
        >
          Todas
        </button>
        {categories.map(cat => (
          <button
            key={cat.id}
            onClick={() => setCat(cat.slug)}
            className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
              categorySlug === cat.slug
                ? 'border-emerald-700 bg-emerald-700 text-white'
                : 'border-slate-200 bg-white text-slate-700 hover:border-emerald-300 hover:text-emerald-800'
            }`}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>
      ) : totalShown === 0 ? (
        <div className="mt-10 rounded-xl border border-dashed border-slate-300 bg-white/60 py-16 text-center">
          <p className="text-slate-600">No se encontraron productos{query ? ` para "${query}"` : ''}.</p>
        </div>
      ) : (
        <div className="mt-10 space-y-14">
          {grouped.map(cat => {
            if (cat.items.length === 0) return null;
            return (
              <section key={cat.id}>
                <CategorySectionHeader title={cat.name} count={cat.items.length} />

                {view === 'marcas' ? (
                  <div className="space-y-8">
                    {cat.brandGroups.map(group => {
                      const brandInfo = brandByName[group.brand];
                      return (
                        <div key={`${cat.id}-${group.brand}`}>
                          <BrandSubHeader
                            brand={group.brand}
                            brandId={group.brandId || brandInfo?.id}
                            logo={brandInfo?.logo}
                            count={group.items.length}
                          />
                          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                            {group.items.map(p => <ProductCard key={p.id} product={p} variant="grid" />)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className={gridClass}>
                    {cat.items.map(p => (
                      <ProductCard key={p.id} product={p} variant={cardVariant} />
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Productos;
